/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { normalizeContentOwnerData } from '../normalizers/normalizeContentOwnerData';

import type {
	useContentOwnerDetailsPanelContentOwnerQuery as useContentOwnerDetailsPanelContentOwnerQuery$data,
	useContentOwnerDetailsPanelContentOwnerQueryVariables as useContentOwnerDetailsPanelContentOwnerQuery$variables,
} from './__types__/useContentOwnerDetailsPanelContentOwnerQuery';

const useContentOwnerDetailsPanelContentOwnerQuery = gql`
	query useContentOwnerDetailsPanelContentOwnerQuery($contentId: ID!, $status: String!) {
		content(id: $contentId, status: [$status]) {
			nodes {
				id
				history {
					ownedBy {
						type
						displayName
						... on KnownUser {
							accountId
						}
						... on User {
							accountId
						}
						profilePicture {
							path
						}
					}
				}
			}
		}
	}
`;
type UseContentOwnerProps = {
	skip?: boolean;
};

export const useContentOwner = ({ skip = false }: UseContentOwnerProps = {}) => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus({ skip });
	const { data, loading, error, refetch } = useQuery<
		useContentOwnerDetailsPanelContentOwnerQuery$data,
		useContentOwnerDetailsPanelContentOwnerQuery$variables
	>(useContentOwnerDetailsPanelContentOwnerQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
		},
		skip: skip || isContentStatusLoading || !!contentStatusError || !contentStatus || !contentId,
	});
	const normalizedData = useMemo(() => normalizeContentOwnerData(data), [data]);

	return {
		data: normalizedData,
		loading,
		error: contentStatusError || error,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
	};
};
